// React is automatically imported with JSX in newer versions
import React, { useEffect, lazy, Suspense, useState, useCallback, useRef, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Trash2, Edit3, X, Search } from 'lucide-react';

import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorNotificationProvider, useErrorNotification } from './contexts/ErrorNotificationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { FeedCacheProvider } from './contexts/FeedCacheContext';
import { FeedPrewarmProvider } from './contexts/FeedPrewarmProvider';
import { RequirementListener } from './hooks/useRequirementListener';
import { triggerBackHandler } from './hooks/useBackButton';
import ErrorBoundary from './components/common/ErrorBoundary';
import { GlobalOfflineIndicator } from './components/common/GlobalOfflineIndicator';
import { LeftSidebar } from './components/layout/LeftSidebar';
import { RightSidebar } from './components/layout/RightSidebar';
import { MobileNav } from './components/layout/MobileNav';
import { BackToTop } from './components/ui/BackToTop';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { QuickActionButton } from './components/ui/QuickActionButton';
import { initPerformanceMonitoring } from './utils/performance';
import { prefetchCoreRoutes } from './utils/routePrefetch';
import './styles/modal.css';

// Requirement CRUD Toast Types
export type RequirementToastType = 'add' | 'update' | 'delete';

export interface RequirementToast {
  id: string;
  type: RequirementToastType;
  message: string;
  timestamp: number;
}

// Requirement Toast Context for CRUD operations
export const useRequirementToast = () => {
  const [toasts, setToasts] = useState<RequirementToast[]>([]);

  const showRequirementToast = useCallback((type: RequirementToastType, message: string) => {
    const id = `req-toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts(prev => [...prev, { id, type, message, timestamp: Date.now() }]);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, showRequirementToast, dismissToast };
};

// Requirement Toast UI Component with distinct CRUD styling
export const RequirementToastContainer: React.FC<{
  toasts: RequirementToast[];
  onDismiss: (id: string) => void;
}> = ({ toasts, onDismiss }) => {
  const getToastStyles = (type: RequirementToastType) => {
    switch (type) {
      case 'add':
        return {
          container: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700',
          icon: 'text-emerald-500',
          title: 'text-emerald-800 dark:text-emerald-200',
          iconComponent: <CheckCircle size={20} />
        };
      case 'update':
        return {
          container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
          icon: 'text-blue-500',
          title: 'text-blue-800 dark:text-blue-200',
          iconComponent: <Edit3 size={20} />
        };
      case 'delete':
        return {
          container: 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700',
          icon: 'text-red-500',
          title: 'text-red-800 dark:text-red-200',
          iconComponent: <Trash2 size={20} />
        };
    }
  };

  const getToastTitle = (type: RequirementToastType) => {
    switch (type) {
      case 'add': return 'Requirement Added';
      case 'update': return 'Requirement Updated';
      case 'delete': return 'Requirement Deleted';
    }
  };

  return (
    <div className="fixed top-[calc(4.5rem+env(safe-area-inset-top,0px))] sm:top-20 right-4 sm:right-6 z-[99999] flex flex-col gap-2.5 items-end pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-md px-2">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const styles = getToastStyles(toast.type);
          return (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 140, scale: 0.92 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{
                opacity: 0,
                x: 140,
                scale: 0.92,
                transition: { duration: 0.22, ease: [0.32, 0, 0.67, 0] }
              }}
              transition={{ type: 'spring', stiffness: 450, damping: 28, mass: 0.75 }}
              className={`pointer-events-auto flex items-center gap-3 px-3.5 py-2.5 rounded-2xl border shadow-xl backdrop-blur-xl w-full ${styles.container}`}
              role="alert"
              aria-live="polite"
            >
              <div className={`flex-shrink-0 ${styles.icon}`}>
                {styles.iconComponent}
              </div>
              <div className="flex-1 min-w-0 pr-1">
                <p className={`text-xs font-bold ${styles.title}`}>
                  {getToastTitle(toast.type)}
                </p>
                <p className="text-xs text-neutral-700 dark:text-neutral-200 truncate font-medium">
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => onDismiss(toast.id)}
                className="flex-shrink-0 p-1.5 rounded-xl text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Dismiss requirement notification"
              >
                <X size={14} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// EXPLORE SEARCH CONTEXT - Global state for hashtag search
// ============================================
export interface ExploreSearchState {
  searchQuery: string;
  activeTab: 'accounts' | 'tags' | 'posts';
  setSearchQuery: (query: string) => void;
  setActiveTab: (tab: 'accounts' | 'tags' | 'posts') => void;
  updateSearchParams: (query: string, tab?: 'accounts' | 'tags' | 'posts') => void;
  clearSearch: () => void;
}

const ExploreSearchContext = createContext<ExploreSearchState | null>(null);

export const useExploreSearch = () => {
  const ctx = useContext(ExploreSearchContext);
  if (!ctx) throw new Error('useExploreSearch must be used within ExploreSearchProvider');
  return ctx;
};

export const ExploreSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState<'accounts' | 'tags' | 'posts'>(
    (searchParams.get('tab') as 'accounts' | 'tags' | 'posts') || 'posts'
  );

  // Sync URL params to state when URL changes (for bookmarking/direct linking)
  useEffect(() => {
    const q = searchParams.get('q') || '';
    const t = (searchParams.get('tab') as 'accounts' | 'tags' | 'posts' | null) || 'posts';
    if (q !== searchQuery) setSearchQuery(q);
    if (t !== activeTab) setActiveTab(t);
  }, [searchParams]);

  // Update URL params when search changes
  const updateSearchParams = useCallback((query: string, tab?: 'accounts' | 'tags' | 'posts') => {
    const nextTab = tab || (query.startsWith('#') ? 'tags' : 'accounts');
    if (query.trim()) {
      setSearchParams({ q: query, tab: nextTab });
    } else {
      setSearchParams({});
    }
    setSearchQuery(query);
    setActiveTab(nextTab);
  }, [setSearchParams]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setActiveTab('posts');
    setSearchParams({});
  }, [setSearchParams]);

  return (
    <ExploreSearchContext.Provider value={{
      searchQuery,
      activeTab,
      setSearchQuery,
      setActiveTab,
      updateSearchParams,
      clearSearch,
    }}>
      {children}
    </ExploreSearchContext.Provider>
  );
};

// ============================================
// EXPLORE SEARCH HEADER - Reusable search bar component
// ============================================
export const ExploreSearchHeader: React.FC = () => {
  const { searchQuery, updateSearchParams, clearSearch } = useExploreSearch();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local state with global state
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    updateSearchParams(localQuery);
    inputRef.current?.blur();
  }, [localQuery, updateSearchParams]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearSearch();
      inputRef.current?.blur();
    }
  }, [clearSearch]);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative flex items-center">
        <Search
          size={20}
          className="absolute left-4 text-neutral-400 dark:text-dark-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search for artwork, artists, or tags..."
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full pl-12 pr-10 py-3 bg-white dark:bg-dark-50 border border-neutral-200 
                     dark:border-dark-200 rounded-2xl focus:outline-none focus:ring-2 
                     focus:ring-primary-500 focus:border-transparent 
                     text-sm md:text-lg text-neutral-900 dark:text-dark-900
                     placeholder:text-neutral-400 dark:placeholder:text-dark-400"
        />
        {localQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-3 p-1 rounded-full hover:bg-neutral-100 
                       dark:hover:bg-dark-100 transition-colors"
            aria-label="Clear search"
          >
            <X size={18} className="text-neutral-500 dark:text-dark-500" />
          </button>
        )}
      </div>
    </form>
  );
};

// ============================================
// EXPLORE TABS - Tab navigation for search results
// ============================================
export const ExploreSearchTabs: React.FC<{
  accountsCount?: number;
  tagsCount?: number;
  postsCount?: number;
}> = ({ accountsCount = 0, tagsCount = 0, postsCount = 0 }) => {
  const { activeTab, setActiveTab, searchQuery, updateSearchParams } = useExploreSearch();

  const tabs = [
    { id: 'accounts' as const, label: 'Accounts', count: accountsCount },
    { id: 'tags' as const, label: 'Tags', count: tagsCount },
    { id: 'posts' as const, label: 'Posts', count: postsCount },
  ];

  const handleTabClick = useCallback((tabId: 'accounts' | 'tags' | 'posts') => {
    setActiveTab(tabId);
    updateSearchParams(searchQuery, tabId);
  }, [setActiveTab, searchQuery, updateSearchParams]);

  if (!searchQuery.trim()) return null;

  return (
    <div className="mb-4 border-b border-neutral-200 dark:border-dark-200">
      <div className="flex gap-4">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`relative px-2 py-2 text-sm font-medium transition-colors
                ${isActive
                  ? 'text-neutral-900 dark:text-dark-900'
                  : 'text-neutral-500 dark:text-dark-500 hover:text-neutral-700 dark:hover:text-dark-700'
                }`}
            >
              {tab.label}
              <span className="ml-1 text-xs text-neutral-400 dark:text-dark-400">
                ({tab.count})
              </span>
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LandingHome = lazy(() => import('./pages/landing-home').then(m => ({ default: m.LandingHome })));
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const Explore = lazy(() => import('./pages/Explore').then(m => ({ default: m.Explore })));
const Requirements = lazy(() => import('./pages/Requirements').then(m => ({ default: m.Requirements })));
const Events = lazy(() => import('./pages/Events').then(m => ({ default: m.Events })));
const JobApplication = lazy(() => import('./pages/JobApplication').then(m => ({ default: m.JobApplication })));
const PostRequirement = lazy(() => import('./pages/PostRequirement').then(m => ({ default: m.PostRequirement })));
const PostEvent = lazy(() => import('./pages/PostEvent').then(m => ({ default: m.PostEvent })));
const JobDetails = lazy(() => import('./pages/JobDetails').then(m => ({ default: m.JobDetails })));
const Services = lazy(() => import('./pages/Services').then(m => ({ default: m.Services })));
const CreateService = lazy(() => import('./pages/CreateService').then(m => ({ default: m.CreateService })));
const Messages = lazy(() => import('./pages/Messages').then(m => ({ default: m.Messages })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const UserProfile = lazy(() => import('./pages/UserProfile').then(m => ({ default: m.default })));
const ArtworkDetail = lazy(() => import('./pages/ArtworkDetail').then(m => ({ default: m.ArtworkDetail })));
const Create = lazy(() => import('./pages/Create').then(m => ({ default: m.Create })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const Goodbye = lazy(() => import('./pages/Goodbye').then(m => ({ default: m.Goodbye })));
const Terms = lazy(() => import('./pages/legal/TermsNew').then(m => ({ default: m.default })));
const Privacy = lazy(() => import('./pages/legal/PrivacyNew').then(m => ({ default: m.default })));
const DeleteAccount = lazy(() => import('./pages/legal/DeleteAccount').then(m => ({ default: m.DeleteAccount })));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines').then(m => ({ default: m.default })));
const ManageApplications = lazy(() => import('./pages/ManageApplications').then(m => ({ default: m.ManageApplications })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const HelpCenter = lazy(() => import('./pages/HelpCenter').then(m => ({ default: m.default })));
const Stories = lazy(() => import('./pages/Stories').then(m => ({ default: m.default })));
const EventMatchesPage = lazy(() => import('./pages/EventMatchesPage').then(m => ({ default: m.EventMatchesPage })));
const EventDetails = lazy(() => import('./pages/EventDetails').then(m => ({ default: m.EventDetails })));
const PortfolioAnalyticsDashboard = lazy(() => import('./components/PortfolioAnalyticsDashboard'));
const TestBlurReveal = lazy(() => import('./pages/TestBlurReveal')); // Test page for blur reveal animation
const TestErrorNotifications = lazy(() => import('./components/TestErrorNotifications').then(m => ({ default: m.TestErrorNotifications }))); // Test page for error notifications

// Event Manager Portal Components
const EventManagerLanding = lazy(() => import('./components/events/EventManagerLanding').then(m => ({ default: m.default || (m as any).EventManagerLanding })));
const EventManagerSignup = lazy(() => import('./components/events/EventManagerSignup').then(m => ({ default: m.default || (m as any).EventManagerSignup })));
const EventManagerProfile = lazy(() => import('./components/events/EventManagerProfile').then(m => ({ default: m.default || (m as any).EventManagerProfile })));
const EventManagerEditProfile = lazy(() => import('./components/events/EventManagerEditProfile').then(m => ({ default: m.default || (m as any).EventManagerEditProfile })));
const EventManagerChat = lazy(() => import('./components/events/EventManagerChat').then(m => ({ default: m.default || (m as any).EventManagerChat })));
const EventManagerDashboard = lazy(() => import('./components/events/EventManagerDashboard').then(m => ({ default: m.default || (m as any).EventManagerDashboard })));
const EventManagerLoginPage = lazy(() => import('./components/events/EventManagerLoginPage').then(m => ({ default: m.default || (m as any).EventManagerLoginPage })));

const SuspenseFallback: React.FC = () => {
  useEffect(() => {
    try {
      window.dispatchEvent(new Event('crevvi-loader-ready'));
    } catch {
      // ignore
    }
  }, []);
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-dark-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-neutral-500 dark:text-dark-300">
        <div className="h-10 w-10 rounded-full border-4 border-primary-200 dark:border-primary-900 border-t-primary-500 dark:border-t-primary-400 animate-spin" />
        <p className="text-sm font-medium">Just a moment…</p>
      </div>
    </div>
  );
};

// auth state is provided via AuthContext
// Entertainment jobs will be loaded on demand when clicking "Load More"

// Layout that includes main site navigation (3-column layout for desktop)
function MainLayout() {
  const { pathname } = useLocation();
  const showQuickAction = pathname === '/' || pathname.startsWith('/profile');
  const isEventMatchesPage = pathname === '/event-matches-page';
  const isExplorePage = pathname === '/explore';

  // Event matches and Explore pages use full-width custom discovery/event layouts
  if (isEventMatchesPage || isExplorePage) {
    return (
      <div className="app-viewport bg-neutral-50 dark:bg-[#080A0F] text-neutral-900 dark:text-white transition-colors duration-200">
        <div className="w-full min-w-0 mx-auto">
          <main
            className="min-h-screen min-w-0 w-full pb-16 lg:pb-0"
          >
            <Outlet />
          </main>
        </div>
        <MobileNav />
        <BackToTop />
      </div>
    );
  }

  // Standard 3-column layout with responsive sidebars
  return (
    <div className="app-viewport bg-neutral-50 dark:bg-[#080A0F] text-neutral-900 dark:text-white transition-colors duration-200">
      <div className="max-w-[1600px] min-w-0 mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0 w-full">
        {/* Left Sidebar - Navigation */}
        <div className="hidden min-w-0 lg:block lg:col-span-3 xl:col-span-2 2xl:col-span-2">
          <LeftSidebar />
        </div>

        {/* Main Content - Fluid & Responsive */}
        <main
          className="col-span-1 min-w-0 lg:col-span-9 xl:col-span-7 2xl:col-span-7 min-h-screen pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:pb-8 px-3 sm:px-4 md:px-6 w-full max-w-full overflow-x-hidden pt-[calc(3.5rem+env(safe-area-inset-top,0px))] lg:pt-6"
        >
          <Outlet />
        </main>

        {/* Right Sidebar - Desktop and Large Screens */}
        <div className="hidden min-w-0 xl:block xl:col-span-3 2xl:col-span-3">
          <RightSidebar />
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNav />

      <BackToTop />
      {showQuickAction && <QuickActionButton />}
    </div>
  );
}

// Auth layout without site navigation (for login/register)
function AuthLayout() {
  return (
    <div
      className="app-viewport bg-neutral-50 dark:bg-[#080A0F] text-neutral-900 dark:text-white transition-colors duration-200"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <Outlet />
    </div>
  );
}

// Messages layout WITHOUT sidebars (hide sidebars only on /messages)
function MessagesLayout() {
  return (
    <div
      className="app-viewport bg-neutral-50 dark:bg-[#080A0F] text-neutral-900 dark:text-white transition-colors duration-200"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="max-w-[1600px] min-w-0 w-full mx-auto h-full">
        <Outlet />
      </div>

      {/* Mobile Navigation */}
      <MobileNav />
    </div>
  );
}

// Using useAuth from AuthContext for a single source of truth

// Guard: only allow authenticated users
function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading, hydrated } = useAuth();
  // Wait until the first auth state has been received to avoid race-condition redirect
  if (!hydrated || loading) return null; // or a splash/loader
  if (!user) {
    // If a logout flow intends to show the goodbye screen, honor it
    let wantGoodbye = false;
    try { wantGoodbye = localStorage.getItem('crevvi_show_goodbye') === '1'; } catch { }
    if (wantGoodbye) return <Navigate to="/goodbye" replace />;
    return <Navigate to="/landing" replace />;
  }
  return children;
}

// Guard: redirect authenticated users away from auth pages
function RedirectIfAuthed({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />; // Landing page after login
  return children;
}

// AppInitializer handles app-level initialization with error notifications
function AppInitializer() {
  const { showWarning } = useErrorNotification();

  // Signal to the static loader that React has hydrated
  useEffect(() => {
    try {
      window.dispatchEvent(new Event('crevvi-app-mounted'));
    } catch {
      // ignore
    }
  }, []);

  // Preload and initialize performance monitoring & prefetch core route chunks in idle time
  useEffect(() => {
    // Initialize performance monitoring
    initPerformanceMonitoring();

    // Prefetch primary route chunks in background idle time
    prefetchCoreRoutes();

    // Ensure browser does not restore scroll on navigation
    const prev = (history as any).scrollRestoration;
    try { (history as any).scrollRestoration = 'manual'; } catch { }
    return () => {
      try { (history as any).scrollRestoration = prev || 'auto'; } catch { }
    };
  }, [showWarning]);

  return null;
}

// Android hardware back button & swipe back gesture handler for native mobile
function AppBackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const lastBackPressRef = useRef<number>(0);

  useEffect(() => {
    let backListener: any = null;

    const registerListener = async () => {
      if (typeof window === 'undefined' || !(window as any).Capacitor?.isNativePlatform?.()) {
        return;
      }

      backListener = await CapacitorApp.addListener('backButton', (data: { canGoBack?: boolean }) => {
        // 1. Highest priority: Close active modal, sheet, or popup from global back handler stack
        const handledByModal = triggerBackHandler();
        if (handledByModal) {
          return;
        }

        // 2. Fallback: If any modal or overlay has data-close-modal in DOM, click it
        const closeBtn = document.querySelector('[data-close-modal]') as HTMLButtonElement | null;
        if (closeBtn) {
          closeBtn.click();
          return;
        }

        // 3. Check history depth & pathname
        const currentPath = locationRef.current.pathname;
        const historyIdx = (window.history.state as any)?.idx;
        const canNavigateBack = (typeof historyIdx === 'number' && historyIdx > 0) || window.history.length > 1 || (data && data.canGoBack);
        const isRootHome = currentPath === '/' || currentPath === '' || currentPath === '/landing';

        if (!isRootHome) {
          // If we are on any subpage, always navigate back to previous page
          navigateRef.current(-1);
        } else if (canNavigateBack && historyIdx > 0) {
          // If on root but entered from somewhere else in history
          navigateRef.current(-1);
        } else {
          // On root home page -> double-tap to exit protection
          const now = Date.now();
          if (now - lastBackPressRef.current < 2000) {
            CapacitorApp.exitApp();
          } else {
            lastBackPressRef.current = now;
            const toastEl = document.createElement('div');
            toastEl.textContent = 'Press back again to exit';
            toastEl.className = 'fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] bg-neutral-900/90 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg pointer-events-none transition-opacity duration-300';
            document.body.appendChild(toastEl);
            setTimeout(() => {
              toastEl.style.opacity = '0';
              setTimeout(() => {
                if (toastEl.parentNode) toastEl.parentNode.removeChild(toastEl);
              }, 300);
            }, 1800);
          }
        }
      });
    };

    registerListener();

    return () => {
      if (backListener && typeof backListener.remove === 'function') {
        backListener.remove();
      }
    };
  }, []); // Registered once on mount so there is no gap/delay during page transitions

  return null;
}

// Adaptive status bar handler that dynamically shifts the screen upwards behind the notification bar
function AdaptiveStatusBarHandler() {
  const location = useLocation();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (typeof window === 'undefined' || !(window as any).Capacitor?.isNativePlatform?.()) {
      return;
    }

    const updateStatusBar = async () => {
      try {
        const path = location.pathname;

        // Shift webview screen upwards to cover the notification bar (True edge-to-edge)
        await StatusBar.show().catch(() => { });
        await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => { });
        await StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => { });

        // 1. Stories route: pure black immersive screen with light icons
        if (path.startsWith('/stories')) {
          await StatusBar.setStyle({ style: Style.Dark });
          return;
        }

        // 2. Landing page: top purple hero section with light icons
        if (path === '/landing') {
          await StatusBar.setStyle({ style: Style.Dark });
          return;
        }

        // 3. Auth pages (Login, Register, Terms, Privacy, Goodbye, Delete Account)
        if (['/login', '/register', '/goodbye', '/terms', '/privacy', '/community-guidelines', '/delete-account'].includes(path)) {
          if (resolvedTheme === 'dark') {
            await StatusBar.setStyle({ style: Style.Dark });
          } else {
            await StatusBar.setStyle({ style: Style.Light });
          }
          return;
        }

        // 4. Standard App Pages with Mobile Header (Home, Explore, Requirements, Events, Services, Messages, Profile, Create, Notifications, etc.)
        if (resolvedTheme === 'dark') {
          // Dark mode: light text/icons
          await StatusBar.setStyle({ style: Style.Dark });
        } else {
          // Light mode: dark text/icons
          await StatusBar.setStyle({ style: Style.Light });
        }
      } catch (err) {
        console.warn('[StatusBar] Update failed:', err);
      }
    };

    updateStatusBar();
  }, [location.pathname, resolvedTheme]);

  return null;
}

function App() {
  const { toasts, showRequirementToast, dismissToast } = useRequirementToast();

  // Expose requirement toast function globally for pages to use
  useEffect(() => {
    (window as any).showRequirementToast = showRequirementToast;
    return () => {
      delete (window as any).showRequirementToast;
    };
  }, [showRequirementToast]);

  return (
    <ThemeProvider>
      <ToastProvider>
        <ErrorNotificationProvider>
          <AuthProvider>
            <NotificationsProvider>
              <UserPreferencesProvider>
                <FeedCacheProvider>
                  <FeedPrewarmProvider>
                    <Router>
                      <AppBackButtonHandler />
                      <AdaptiveStatusBarHandler />
                      <GlobalOfflineIndicator />
                      <RequirementToastContainer toasts={toasts} onDismiss={dismissToast} />
                      <AppInitializer />
                      <RequirementListener />
                      <ScrollToTop />
                      <Suspense fallback={<SuspenseFallback />}>
                        <ErrorBoundary>
                          <Routes>
                            {/* Public Landing Page (no auth required, no Navigation, edge-to-edge) */}
                            <Route path="/landing" element={<RedirectIfAuthed><LandingHome /></RedirectIfAuthed>} />

                            {/* Auth-only routes (no Navigation) */}
                            <Route element={<AuthLayout />}>
                              <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
                              <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
                            </Route>

                            {/* Standalone Edge-to-Edge Policy & Utility Routes */}
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                            <Route path="/delete-account" element={<DeleteAccount />} />
                            <Route path="/goodbye" element={<Goodbye />} />

                            {/* Main app routes with Navigation (protected) */}
                            <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
                              <Route path="/" element={<Home />} />
                              <Route path="/explore" element={
                                <ExploreSearchProvider>
                                  <Explore />
                                </ExploreSearchProvider>
                              } />
                              <Route path="/requirements" element={<Requirements />} />
                              <Route path="/requirements/apply/:jobId" element={<JobApplication />} />
                              <Route path="/apply/:jobId" element={<JobApplication />} />
                              <Route path="/requirements/post" element={<PostRequirement />} />
                              <Route path="/requirements/:jobId" element={<JobDetails />} />
                              <Route path="/requirements/:jobId/applications" element={<ManageApplications />} />
                              <Route path="/events" element={<Events />} />
                              <Route path="/events/:id" element={<EventDetails />} />
                              <Route path="/event-matches" element={<Events />} />
                              <Route path="/event-matches-page" element={<EventMatchesPage />} />
                              <Route path="/events/post" element={<PostEvent />} />
                              <Route path="/services" element={<Services />} />
                              <Route path="/services/new" element={<CreateService />} />
                              <Route path="/profile" element={<Profile />} />
                              <Route path="/profile/:username" element={<UserProfile />} />
                              <Route path="/artists/:id" element={<UserProfile />} />
                              <Route path="/artwork/:id" element={<ArtworkDetail />} />
                              <Route path="/post/:id" element={<ArtworkDetail />} />
                              <Route path="/create" element={<Create />} />
                              <Route path="/notifications" element={<Notifications />} />
                              <Route path="/help" element={<HelpCenter />} />
                              <Route path="/portfolio-analytics" element={<PortfolioAnalyticsDashboard />} />
                              <Route path="/jobs" element={<Navigate to="/requirements" replace />} />
                              <Route path="/jobs/post" element={<Navigate to="/requirements/post" replace />} />
                              <Route path="/jobs/:jobId" element={<Navigate to="/requirements" replace />} />
                            </Route>

                            {/* Messages route without Navigation (protected) */}
                            <Route element={<RequireAuth><MessagesLayout /></RequireAuth>}>
                              <Route path="/messages" element={<Messages />} />
                              <Route path="/messages/:conversationId" element={<Messages />} />
                            </Route>

                            {/* Stories route */}
                            <Route path="/stories/:username" element={
                              <RequireAuth>
                                <Stories />
                              </RequireAuth>
                            } />

                            {/* Event Manager Portal Routes - Public (no main user auth required) */}
                            <Route element={<AuthLayout />}>
                              <Route path="/event-manager" element={<EventManagerLanding />} />
                              <Route path="/event-manager/login" element={<EventManagerLoginPage />} />
                              <Route path="/event-manager/signup" element={<EventManagerSignup />} />
                              <Route path="/event-manager/dashboard" element={<EventManagerDashboard />} />
                              <Route path="/event-manager/dashboard/:eventManagerId" element={<EventManagerDashboard />} />
                              <Route path="/event-manager/chat/:chatId" element={<EventManagerChat />} />
                              <Route path="/event-manager/profile" element={<EventManagerProfile />} />
                              <Route path="/event-manager/edit-profile" element={<EventManagerEditProfile />} />
                              {import.meta.env.DEV && (
                                <>
                                  <Route path="/test-blur-reveal" element={<TestBlurReveal />} />
                                  <Route path="/test-error-notifications" element={<TestErrorNotifications />} />
                                </>
                              )}
                            </Route>

                            {/* Admin owns its full-screen layout and safe-area handling */}
                            <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
                            <Route path="/admin/*" element={<Admin />} />
                          </Routes>
                        </ErrorBoundary>
                      </Suspense>
                    </Router>
                  </FeedPrewarmProvider>
                </FeedCacheProvider>
              </UserPreferencesProvider>
            </NotificationsProvider>
          </AuthProvider>
        </ErrorNotificationProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;